/**
 * Copy for the accessories catalogue.
 *
 * STATUS: PLACEHOLDER, in the same sense as `content/stats.ts`. Kabex has not
 * supplied a parts catalogue, a price list or lead times (A17, docs/
 * information-architecture.md:91), so every figure the catalogue shows is
 * sample data and the page says so on its face. None of these claims may be
 * strengthened, and no price here may survive into a production build without
 * Kabex's own list behind it.
 *
 * What is *not* placeholder is the shape: the categories, the fitment line and
 * the lead time are the three things an accessories shopper decides on, and
 * they are what the backend has to supply when it arrives.
 */
import { type IconKey } from '@/content/site';
import { type AccessorySource, type KnownAccessoryCategory } from '@/services/accessories/types';

export type AccessoryCategoryMeta = {
  value: KnownAccessoryCategory;
  label: string;
  /** One line for the category tile. */
  blurb: string;
  icon: IconKey;
};

/**
 * The seven shelves this codebase has artwork for, in the order the home page
 * shows them: wheels and tyres first, then what goes on the outside of the car,
 * then the cabin, then the things that are bought on a schedule.
 *
 * NOT the list of shelves any more. Since 2026-10-26 the shelves themselves are
 * rows in `accessory_categories`, and /accessories draws its tiles from the
 * catalogue's own facets — name, order and count included — so a shelf an
 * administrator adds appears there without a deploy. What this table still owns
 * is the icon, the plate texture and the photograph, none of which can be typed
 * into a form; and it is what the static home-page band is built from, since
 * that band fetches nothing.
 */
export const accessoryCategories: readonly AccessoryCategoryMeta[] = [
  {
    value: 'wheels-tyres',
    label: 'Wheels & tyres',
    blurb: 'Alloys, rims and tyres sized to the car you imported.',
    icon: 'car',
  },
  {
    value: 'exterior',
    label: 'Exterior & styling',
    blurb: 'Bull bars, roof racks, running boards and body kits.',
    icon: 'route',
  },
  {
    value: 'interior',
    label: 'Interior & comfort',
    blurb: 'Seat covers, mats, trim and cabin fittings.',
    icon: 'customer',
  },
  {
    value: 'electronics',
    label: 'Electronics',
    blurb: 'Head units, reverse cameras, trackers and dash cams.',
    icon: 'updates',
  },
  {
    value: 'protection',
    label: 'Security & protection',
    blurb: 'Alarms, immobilisers, under-guards and covers.',
    icon: 'shield',
  },
  {
    value: 'performance',
    label: 'Performance',
    blurb: 'Suspension, brakes and exhaust for the roads here.',
    icon: 'speed',
  },
  {
    value: 'maintenance',
    label: 'Service parts',
    blurb: 'Filters, pads, belts and fluids for scheduled service.',
    icon: 'clearing',
  },
];

// `accessoryCategoryLabels` was removed with the sample catalogue: nothing
// imported it, and a label map keyed on a closed union is exactly the thing a
// shelf added from Kabex Hub would be missing from. A part carries its own
// `categoryLabel`; `categoryMeta.categoryLabel()` covers a bare slug.

/**
 * Where the part came from, in the words a buyer uses.
 *
 * Genuine and OEM are not the same claim and the catalogue must not blur them:
 * genuine carries the carmaker's own box, OEM is the same factory without it.
 * Getting that wrong is a consumer-protection problem before it is a UX one.
 */
export const accessorySourceLabels: Record<AccessorySource, string> = {
  genuine: 'Genuine',
  oem: 'OEM',
  aftermarket: 'Aftermarket',
};

export const accessorySourceHelp: Record<AccessorySource, string> = {
  genuine: 'Supplied in the carmaker’s own packaging.',
  oem: 'Made by the original factory, without the carmaker’s box.',
  aftermarket: 'Made by a third party to fit the same car.',
};

/**
 * The proof points across the foot of the opening band.
 *
 * Three, like the import page's, and each one has to hold for a visitor who
 * already owns the car — which is who this page is for. Accessories are not
 * shipped with an import; they are sold, delivered and fitted on their own.
 * `AccessoryStock` has said so since the catalogue was built (held in Kampala,
 * in on the next shipment, or ordered against a request), so the band may not
 * borrow the import journey's promises, and two of these three used to.
 *
 * What they say instead: we check the part against the car you have, the part
 * is genuine or OEM rather than a market copy, and it reaches you wherever you
 * are. The middle one is the objection that actually loses the sale in Kampala
 * — UNBS puts roughly half of what is on the market as fake or substandard —
 * and it is also the only claim here the catalogue can already evidence: every
 * row carries `source`, and `accessorySourceLabels` keeps genuine and OEM
 * apart rather than blurring them.
 *
 * Fitting is not dropped, it is moved: it belongs in the lede, where it reads
 * as a service you can come in for, not as something that happens to you on
 * the day you collect a car. Delivery, same-day fitting and the workshop were
 * all confirmed by Kabex on 2026-09-30; nothing here is a new promise.
 */
export const accessoryHighlights = [
  { icon: 'search', label: 'Checked against your chassis number' },
  { icon: 'verified', label: 'Genuine and OEM parts, never copies' },
  // The truck stays on the claim that is actually about a truck.
  { icon: 'clearing', label: 'Delivered anywhere in Uganda' },
] as const;
